import moment from "moment";
import { CHANGE_ATTRIBUTE_NAME_MAP, NO_RECENT_LEAVE_CHANGES_MESSAGE, noDataPlaceholder } from "./constants.js";
import { PREVIOUS_DAY_SUBVIEWS, PREVIOUS_DAY_PERIODS } from "./ExcelConstants.js";
export const EXCEL_DECIMAL_FORMAT = "0.00";
export const isDecimalExportColumn = (column = {}) => typeof column.sortingMethod === "string" &&
    column.sortingMethod.trim().toLowerCase() === "decimal";
export const resolveExcelExportNumericValue = (value) => {
    if (typeof value === "number") {
        return Number.isFinite(value) ? { parsedValue: value } : null;
    }
    if (typeof value !== "string") {
        return null;
    }
    const trimmedValue = value.trim();
    if (!trimmedValue || trimmedValue.toLowerCase() === "no access") {
        return null;
    }
    const cleanedValue = trimmedValue.replace(/[$,\s]/g, "");
    if (!/^-?\d*\.?\d+$/.test(cleanedValue)) {
        return null;
    }
    const parsedValue = Number(cleanedValue);
    return Number.isFinite(parsedValue) ? { parsedValue } : null;
};
export const shouldUsePreviousDay = (subViewName, periodName) => PREVIOUS_DAY_SUBVIEWS.includes(subViewName) &&
    PREVIOUS_DAY_PERIODS.includes(periodName);
export const RemoveYear = (dateobject) => {
    if (dateobject === undefined || dateobject === "" || dateobject === null) {
        return null;
    }
    const dateStr = typeof dateobject === "number" ? String(dateobject) : dateobject;
    return dateStr.includes("2099") ? noDataPlaceholder : dateStr;
};
const IN_ERROR_REASON_REGEX = /(?:\bmuerr\b|\bmuper\b|in[-\s]*error|up[-\s]*in[-\s]*error)/i;
export const isInErrorReason = (value) => {
    if (value === null || value === undefined)
        return false;
    if (Array.isArray(value)) {
        return value.some(isInErrorReason);
    }
    const text = String(value).trim();
    if (!text)
        return false;
    return IN_ERROR_REASON_REGEX.test(text);
};
export const hasInErrorReason = (...values) => values.some((value) => isInErrorReason(value));
const toNonNegativeNumber = (value) => {
    if (typeof value !== "number" && typeof value !== "string") {
        return 0;
    }
    const number = Number.parseFloat(String(value));
    return Number.isFinite(number) ? Math.max(number, 0) : 0;
};
export const formatTreatmentDetails = (treatmentDetails, leaveSourceSystem) => {
    if (!treatmentDetails) {
        return null;
    }
    const scheduleTypeDesc = treatmentDetails?.scheduleTypeDesc?.trim();
    const simpleScheduleTypes = ["As Needed", "Daily", "None", "Other", "Unknown"];
    if (scheduleTypeDesc && simpleScheduleTypes.includes(scheduleTypeDesc)) {
        return scheduleTypeDesc;
    }
    if (!treatmentDetails.periodUnitTypeDesc?.trim() || !treatmentDetails.recoveryDurationUnitTypeDesc?.trim()) {
        return null;
    }
    const isFineosOfficeVisit = leaveSourceSystem === "FNEOS" && scheduleTypeDesc === "Office Visit Episodic";
    const isNavilinkTreatment = leaveSourceSystem === "NVLNK" && scheduleTypeDesc === "Specific Treatment Frequency";
    if (isFineosOfficeVisit || isNavilinkTreatment) {
        return (toNonNegativeNumber(treatmentDetails.periodQty) +
            " " +
            treatmentDetails.periodUnitTypeDesc +
            ", " +
            toNonNegativeNumber(treatmentDetails.treatmentQty) +
            " times every " +
            toNonNegativeNumber(treatmentDetails.recoveryDurationQty) +
            " " +
            treatmentDetails.recoveryDurationUnitTypeDesc);
    }
    return null;
};
export const formatEpisodeDetails = (episodeDetails, leaveSourceSystem) => {
    if (!episodeDetails) {
        return null;
    }
    const scheduleTypeDesc = episodeDetails?.scheduleTypeDesc?.trim();
    const simpleScheduleTypes = ["As Needed", "Daily", "None", "Other", "Unknown"];
    if (scheduleTypeDesc && simpleScheduleTypes.includes(scheduleTypeDesc)) {
        return scheduleTypeDesc;
    }
    if (!episodeDetails.periodUnitTypeDesc?.trim() || !episodeDetails.recoveryDurationUnitTypeDesc?.trim()) {
        return null;
    }
    const isFineosEpisodic = leaveSourceSystem === "FNEOS" &&
        (scheduleTypeDesc === "Incapacity Episodic" || scheduleTypeDesc === "Episodic");
    const isNavilinkEpisodic = leaveSourceSystem === "NVLNK" && scheduleTypeDesc === "Specific Episode Frequency";
    if (isFineosEpisodic || isNavilinkEpisodic) {
        return (toNonNegativeNumber(episodeDetails.periodQty) +
            " " +
            episodeDetails.periodUnitTypeDesc +
            ", " +
            toNonNegativeNumber(episodeDetails.episodeQty) +
            " times every " +
            toNonNegativeNumber(episodeDetails.recoveryDurationQty) +
            " " +
            episodeDetails.recoveryDurationUnitTypeDesc);
    }
    return null;
};
const getStartOfToday = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};
export const isActiveDigitalAddress = (digitalAddressEndDt, asOfDate = getStartOfToday()) => {
    if (!digitalAddressEndDt) {
        return true;
    }
    const parsedEndDate = new Date(digitalAddressEndDt);
    if (Number.isNaN(parsedEndDate.getTime())) {
        return false;
    }
    return parsedEndDate >= asOfDate;
};
export const getLatestChangeReason = (leave) => {
    const changeDate = leave.lastChangedAttribute?.changeDate;
    const changeAttribute = leave.lastChangedAttribute?.attributeName;
    if (changeDate && isDateWithinLast30Days(changeDate)) {
        return CHANGE_ATTRIBUTE_NAME_MAP[changeAttribute] ?? NO_RECENT_LEAVE_CHANGES_MESSAGE;
    }
    return NO_RECENT_LEAVE_CHANGES_MESSAGE;
};
export const getLatestChangeDate = (leave) => {
    const changeDate = leave.lastChangedAttribute?.changeDate;
    if (changeDate && isDateWithinLast30Days(changeDate)) {
        return changeDate;
    }
    return NO_RECENT_LEAVE_CHANGES_MESSAGE;
};
export const isDateWithinLast30Days = (dateString) => {
    try {
        const formats = ["MM/DD/YYYY"];
        const date = moment(dateString, formats, true);
        if (!date.isValid()) {
            return false;
        }
        const today = moment().startOf("day");
        const thirtyDaysAgo = today.clone().subtract(30, "days");
        return date.isBetween(thirtyDaysAgo, today, "day", "[]");
    }
    catch (e) {
        return false;
    }
};
